import App from "@/App";
import RouteErrorBoundary, { withRouteErrorBoundary } from "@/app/components/core/RouteErrorBoundary";
import { useAppDispatch } from "@/app_redux/hooks/root_hook";
import React, { useEffect } from "react";
import toast, { Toaster } from "react-hot-toast";
import { RouterProvider, createBrowserRouter, redirect } from "react-router-dom";
import LandingPage from "./routes/public_routes/LandingPage";
import LoginPage from "./routes/public_routes/LoginPage";
import CustomerProfilePage from "./routes/auth_routes/CustomerProfilePage/";
import CustomerAddPage from "./routes/auth_routes/CustomerProfilePage/CustomerAddPage";
import CustomerDetailsPage from "./routes/auth_routes/CustomerProfilePage/CustomerDetailsPage";
import CustomerProfileSearchPage from "./routes/auth_routes/CustomerProfilePage/CustomerProfileSearchPage";
import UserLevelCouponPage from "./routes/auth_routes/CustomerProfilePage/UserLevelCouponPage";
import { setAuthorization, setOAuthUser } from "@/app_redux/reducers/auth_reducer";
import AccountTab from "./components/routes/auth_routes/CustomerProfile/CustomerDetails/Headers/AccountTab";
import ActivityTab from "./components/routes/auth_routes/CustomerProfile/CustomerDetails/Headers/ActivityTab";
import AppointmentTab from "./components/routes/auth_routes/CustomerProfile/CustomerDetails/Headers/AppointmentTab";
import CertificatesTab from "./components/routes/auth_routes/CustomerProfile/CustomerDetails/Headers/CertificatesTab";
import EMRTab from "./components/routes/auth_routes/CustomerProfile/CustomerDetails/Headers/EMRTab";
import LabTestTab from "./components/routes/auth_routes/CustomerProfile/CustomerDetails/Headers/LabTestTab";
import LabInterpretationTab from "./components/routes/auth_routes/CustomerProfile/CustomerDetails/Headers/LabInterpretationTab";
import OtherInformationTab from "./components/routes/auth_routes/CustomerProfile/CustomerDetails/Headers/OtherInformationTab";
import VaccinationTab from "./components/routes/auth_routes/CustomerProfile/CustomerDetails/Headers/VaccinationTab";
import CustomerProfileIndexPage from "./routes/auth_routes/CustomerProfilePage/CustomerProfileIndexPage";
import SlotsPage from "./routes/auth_routes/SlotsPage";
import PatientWaitingListPage from "./routes/auth_routes/PatientWaitingListPage";
import AtHomeServicesPage from "./routes/auth_routes/AtHomeServicesPage";
import { ISessionUserDetails } from "@/api_framework/api_modals/FirebaseLogin";
import AddPetForCustomerForm from "./components/routes/auth_routes/CustomerProfile/AddCustomer/AddPetForCustomerForm";
import EditCustomerInfoForm from "./components/routes/auth_routes/CustomerProfile/AddCustomer/EditCustomerInfoForm";
import EditCustomerPetInfoForm from "./components/routes/auth_routes/CustomerProfile/AddCustomer/EditCustomerPetInfoForm";
import { IPatientWaitingListFiltersParamsBody } from "@/api_framework/api_modals/Patients";
import { useClarity } from "./hooks/useClarity";
import { useConfiguration } from "./hooks/useConfiguration";
import { useRouter } from "./hooks/useRouter";
import AvailableAndBookedSlotsComponent from "./components/routes/auth_routes/SlotsScreen/AvailableAndBookedSlotsComponent";
import AppointmentHairCutToBathComponent from "./components/routes/auth_routes/Appointments/AppointmentHairCutToBathComponent";
import AppointmentOverBookingComponent from "./components/routes/auth_routes/Appointments/AppointmentOverBookingComponent";
import AppointmentOverBookingV2Component from "./components/routes/auth_routes/Appointments/AppointmentOverBookingV2Component";
import {
  AppointmentOverBookingV2CreateRequestRoute,
  AppointmentOverBookingV2UpdateRequestRoute,
} from "./components/routes/auth_routes/Appointments/AppointmentOverBookingV2Component/RequestModal/RequestRoutes";
import CentralOppsModuleComponent from "./components/routes/auth_routes/OrderManagement/CentralModuleComponent";
import SlotsConfirmationComponent from "./components/routes/auth_routes/SlotsScreen/ComfirmationComponent";
import OverBookingSlotsComponent from "./components/routes/auth_routes/SlotsScreen/OverBookingSlotsComponent";
import AppointmentsPage from "./routes/auth_routes/AppointmentPage";
import InvalidAccessPage from "./routes/error_routes/InvalidAccessPage";
import InvalidLoginPage from "./routes/error_routes/invalidLoginPage";
import { setPWLFilters } from "@/app_redux/reducers/slices/auth/patient_waiting_list_slice";
import { useNetworkState } from "@uidotdev/usehooks";
import AppointmentShiftAppointment from "./components/routes/auth_routes/Appointments/AppointmentShiftAppointment";
import CheckSlotsComponent from "./components/routes/auth_routes/Appointments/CheckSlots";
import ProductRequirementForm from "./components/routes/auth_routes/Appointments/ProductRequirementForm";
import OverBookingPermisionListComponent from "./components/routes/auth_routes/Appointments/OverBookingPermisionListComponent";
import ConfigurationCitySetup from "./components/routes/auth_routes/Configuration/CitySetup";
import ConfigurationCityOnboard from "./components/routes/auth_routes/Configuration/CitySetup/CityOnboard";
import ConfigurationClinicSetup from "./components/routes/auth_routes/Configuration/ClinicSetup";
import ConfigurationClinicOnboard from "./components/routes/auth_routes/Configuration/ClinicSetup/ClinicOnboard";
import ClinicImagesSetup from "./components/routes/auth_routes/Configuration/ClinicSetup/ClinicImagesSetup";
import ConfigurationEquipmentSetup from "./components/routes/auth_routes/Configuration/EquipmentSetup";
import ConfigurationEquipmentOnboard from "./components/routes/auth_routes/Configuration/EquipmentSetup/EquipmentOnboard";
import ConfigurationStaffCategorySetup from "./components/routes/auth_routes/Configuration/StaffCategorySetup";
import ConfigurationStaffCategoryOnboard from "./components/routes/auth_routes/Configuration/StaffCategorySetup/StaffCategoryOnboard";
import ConfigurationStaffSetup from "./components/routes/auth_routes/Configuration/StaffSetup";
import ConfigurationGroomerSetup from "./components/routes/auth_routes/Configuration/GroomerSetup";
import ConfigurationParavetSetup from "./components/routes/auth_routes/Configuration/ParavetSetup";
import StaffOnboardingList from "./components/routes/auth_routes/Configuration/StaffOnboarding";
import ClinicMappingList from "./components/routes/auth_routes/Configuration/StaffOnboarding/ClinicMappingList";
import StaffOnboardForm from "./components/routes/auth_routes/Configuration/StaffOnboarding/StaffOnboardForm";
import ParavetOnboardComponent from "./components/routes/auth_routes/Configuration/ParavetSetup/ParavetOnboardComponent";
import GroomerOnboardComponent from "./components/routes/auth_routes/Configuration/GroomerSetup/GroomerOnboardComponent";
import ConfigurationStaffOnboard from "./components/routes/auth_routes/Configuration/StaffSetup/StaffOnboard";
import MembershipDetailsTab from "./components/routes/auth_routes/CustomerProfile/CustomerDetails/Headers/MembershipDetailsTab";
import AddNewShiftComponent from "./components/routes/auth_routes/RotaManagement/AddNewShiftComponent";
import RotaCalenderComponent from "./components/routes/auth_routes/RotaManagement/RotaCalenderComponent";
import RotaErrorListComponent from "./components/routes/auth_routes/RotaManagement/RotaErrorListComponent";
import ShiftDetailsComponent from "./components/routes/auth_routes/RotaManagement/SetupStandardShiftComponent";
import BookingFeeConfigComponent from "./components/routes/auth_routes/RotaManagement/BookingFeeConfigComponent";
import BookingFeeConfigListComponent from "./components/routes/auth_routes/RotaManagement/BookingFeeConfigListComponent";
import RotaManagementComponent from "./components/routes/auth_routes/RotaManagement/index";
import RemoveSlotsComponent, {
  RemoveSlotsCreateRequestRoute,
} from "./components/routes/auth_routes/RotaManagement/RemoveSlotsComponent";
import BookedSlotsComponent from "./components/routes/auth_routes/SlotsScreen/BookedSlotsComponent";
import { useModalActions } from "./hooks/ui_hooks/useModalActions";
import ConfigurationPage from "./routes/auth_routes/ConfigurationPage";
import ConfigurationIndexPage from "./routes/auth_routes/ConfigurationPage/ConfigurationIndexPage";
import RotaManagementComponentIndex from "./routes/auth_routes/RotaManagement";
import { setMappedCityId, setMappedClinicData } from "@/app_redux/reducers/slices/ui_states_slice";
import CheckAvailableSlotsComponent from "./components/routes/auth_routes/Appointments/CheckSlots/CheckAvailableSlots";
import CheckBookedSlotsComponent from "./components/routes/auth_routes/Appointments/CheckSlots/CheckBookedSlots";
import CartCreationComponent from "./components/routes/auth_routes/CartManagement/CartCreationComponent";
import CartListComponent from "./components/routes/auth_routes/CartManagement/CartListComponent";
import ChargeableBookingsList from "./components/routes/auth_routes/ChargeableBookingsList";
import ConfigurationRolesSetup from "./components/routes/auth_routes/Configuration/RolesSetup";
import ConfigurationRolesOnboard from "./components/routes/auth_routes/Configuration/RolesSetup/RolesOnboard";
import WarehouseOnboardComponent from "./components/routes/auth_routes/Configuration/WarehouseOnboardComponent";
import WarehouseAddComponent from "./components/routes/auth_routes/Configuration/WarehouseOnboardComponent/WarehouseAddComponent";
import FormTabComponent from "./components/routes/auth_routes/CustomerProfile/CustomerDetails/Headers/FormTab";
import InvoiceTab from "./components/routes/auth_routes/CustomerProfile/CustomerDetails/Headers/InvoiceTab";
import TicketTab from "./components/routes/auth_routes/CustomerProfile/CustomerDetails/Headers/TicketTab";
import BOMSetupListing from "./components/routes/auth_routes/InventoryManagement/BOMSetupComponent";
import BOMSetupOnboard from "./components/routes/auth_routes/InventoryManagement/BOMSetupComponent/BOMSetupOnboard";
import BatchSetupComponent from "./components/routes/auth_routes/InventoryManagement/BatchSetupComponent";
import BatchAddComponent from "./components/routes/auth_routes/InventoryManagement/BatchSetupComponent/BatchAddComponent";
import TransferInventory from "./components/routes/auth_routes/InventoryManagement/ClinicToClinicTransfer/TransferInventory";
import TransferOrderDetails from "./components/routes/auth_routes/InventoryManagement/ClinicToClinicTransfer/TransferOrderDetails";
import TransferOrderReadOnly from "./components/routes/auth_routes/InventoryManagement/ClinicToClinicTransfer/TransferOrderReadOnly";
import ClinicToDoctorTransferComponent from "./components/routes/auth_routes/InventoryManagement/ClinicToDoctorTransfer";
import DoctorTransferInventory from "./components/routes/auth_routes/InventoryManagement/ClinicToDoctorTransfer/TransferInventory";
import BadInventoryRequests from "./components/routes/auth_routes/InventoryManagement/InventoryMovement/BadInventoryRequests";
import GoodInventoryActions from "./components/routes/auth_routes/InventoryManagement/InventoryMovement/GoodInventoryActions";
import InventorySnapshot from "./components/routes/auth_routes/InventoryManagement/InventoryMovement/InventorySnapshot";
import DoctorInventory from "./components/routes/auth_routes/InventoryManagement/InventoryMovement/DoctorInventory";
import ManufacturerSetupComponent from "./components/routes/auth_routes/InventoryManagement/ManufacturerSetupComponent";
import ManufacturerAddComponent from "./components/routes/auth_routes/InventoryManagement/ManufacturerSetupComponent/ManufacturerAddComponent";
import GoodsReceiptNoteGrnDoneComponent from "./components/routes/auth_routes/InventoryManagement/OrderPurchaseComponent/GoodsReceiptNoteGrnDoneComponent";
import GoodsReceiptNotePendingGoodsComponent from "./components/routes/auth_routes/InventoryManagement/OrderPurchaseComponent/GoodsReceiptNotePendingGoodsComponent";
import OrderPurchaseAddComponent from "./components/routes/auth_routes/InventoryManagement/OrderPurchaseComponent/OrderPurchaseAddComponent";
import OrderPurchaseListComponent from "./components/routes/auth_routes/InventoryManagement/OrderPurchaseComponent/OrderPurchaseListComponent";
import ProductAddComponent from "./components/routes/auth_routes/InventoryManagement/ProductSetupComponent/ProductAddComponent";
import ProductPersonaDiscountComponent from "./components/routes/auth_routes/InventoryManagement/ProductSetupComponent/ProductPersonaDiscountComponent";
import ProductListComponent from "./components/routes/auth_routes/InventoryManagement/ProductSetupComponent/ProductListComponent";
import ServiceAddComponent from "./components/routes/auth_routes/InventoryManagement/ServiceSetupComponent/ServiceAddComponent";
import ServiceListComponent from "./components/routes/auth_routes/InventoryManagement/ServiceSetupComponent/ServiceListComponent";
import StockIncompleteForm from "./components/routes/auth_routes/InventoryManagement/StockIncompleteFormComponent";
import TaxAddComponent from "./components/routes/auth_routes/InventoryManagement/TaxSetupComponent/TaxAddComponent";
import TaxSetupComponent from "./components/routes/auth_routes/InventoryManagement/TaxSetupComponent/index";
import VendorAddComponent from "./components/routes/auth_routes/InventoryManagement/VendorAddComponent";
import VendorsListComponent from "./components/routes/auth_routes/InventoryManagement/VendorsListComponent";
import QuickCommercePincodeSetup from "./components/routes/auth_routes/QuickCommerce/PincodeSetup";
import ConfigurationAddPincodeOnboard from "./components/routes/auth_routes/QuickCommerce/PincodeSetup/PincodeOnboard";
import PincodeWeatherSetup from "./components/routes/auth_routes/QuickCommerce/PincodeWeatherSetup";
import HomeTileConfigSetup from "./components/routes/auth_routes/QuickCommerce/HomeTileConfig";
import HomeTileOnboard from "./components/routes/auth_routes/QuickCommerce/HomeTileConfig/HomeTileOnboard";
import ClinicTagConfigSetup from "./components/routes/auth_routes/QuickCommerce/ClinicTagConfig";
import ClinicTagOnboard from "./components/routes/auth_routes/QuickCommerce/ClinicTagConfig/ClinicTagOnboard";
import StoreTimingSetup from "./components/routes/auth_routes/Configuration/StoreTimingSetup";
import StaffCalendarComponent from "./components/routes/auth_routes/StaffCalendar";
import ServiceDeskRaiseIssue from "./components/routes/auth_routes/VeticServiceDesk/ServiceDeskRaiseIssue";
import ServiceDeskissueList from "./components/routes/auth_routes/VeticServiceDesk/ServiceDeskissueList";
import CartPage from "./routes/auth_routes/CartPage";
import InventoryManagementPage from "./routes/auth_routes/InventoryManagementPage";
import OrderManagementIndexPage from "./routes/auth_routes/OrderManagementPage";
import StaffCalendarComponentIndex from "./routes/auth_routes/StaffCalendarPage";
import VMSSetupPage from "./routes/auth_routes/VMSSetupPage";
import VeticServiceDeskIndex from "./routes/auth_routes/VeticServiceDeskIndex";
import CouponCreationComponent from "./components/routes/auth_routes/CouponService/CouponCreationComponent";
import CouponListComponent from "./components/routes/auth_routes/CouponService/CouponListComponent";
import CouponServiceIndexPage from "./routes/auth_routes/CouponServiceIndexPage";
import LogoutPage from "./routes/public_routes/LogoutPage";
import CartBillingComponent from "./components/routes/auth_routes/CartManagement/CartBillingComponent";
import ItemGroupSetupListing from "./components/routes/auth_routes/GroupItems";
import ItemGroupSetupOnboard from "./components/routes/auth_routes/GroupItems/GroupItemsOnboard";
import SurgePricingList from "./components/routes/auth_routes/SurgePricing";
import SurgeRuleOnboard from "./components/routes/auth_routes/SurgePricing/SurgeRuleOnboard";
import SurgePriceCheck from "./components/routes/auth_routes/SurgePricing/SurgePriceCheck";
import CategorySetupListing from "./components/routes/auth_routes/InventoryManagement/CategorySetupComponent";
import CategorySetupOnboard from "./components/routes/auth_routes/InventoryManagement/CategorySetupComponent/CategorySetupOnboard";
import CategorySubCategoryMapping from "./components/routes/auth_routes/InventoryManagement/CategorySubCategoryMapping";
import CategorySubCategoryMappingOnboard from "./components/routes/auth_routes/InventoryManagement/CategorySubCategoryMapping/CategorySubCategoryMappingOnboard";
import ClinicToClinicTransfer from "./components/routes/auth_routes/InventoryManagement/ClinicToClinicTransfer";
import SubCategorySetupListing from "./components/routes/auth_routes/InventoryManagement/SubCategorySetupComponent";
import SubCategorySetupOnboard from "./components/routes/auth_routes/InventoryManagement/SubCategorySetupComponent/SubCategprySetupOnboard";
import MembershipSetup from "./components/routes/auth_routes/MembershipService";
import MembershipCreationComponent from "./components/routes/auth_routes/MembershipService/MembershipCreationComponent";
import WhitelistMobile from "./components/routes/auth_routes/WhitelistMobile";
import ClinicToClinicTranfer from "./routes/auth_routes/ClinicToClinicTranfer";
import MembershipSetupIndexPage from "./routes/auth_routes/MembershipSetupIndexPage";
import MembershipExtensionIndexPage from "./routes/auth_routes/MembershipExtensionIndexPage";
import MembershipExtensionSetup from "./components/routes/auth_routes/MembershipExtensionService";
import MembershipExtensionCreationPage from "./components/routes/auth_routes/MembershipExtensionService/MembershipExtensionCreationPage";
import WhiteListComponentIndex from "./routes/auth_routes/WhitelistMobilePage";
import MappedClinicAndCityPage from "./routes/error_routes/MapClinicAndCityPage";
import InvoiceListingComponent from "./components/routes/auth_routes/CartManagement/WalletHistoryComponent/InvoiceListingComponent";
import AddCreditComponent from "./components/routes/auth_routes/CartManagement/WalletHistoryComponent/AddCreditComponent";
import TransactionHistoryComponent from "./components/routes/auth_routes/CartManagement/WalletHistoryComponent/TransactionHistoryComponent";
import CreditListComponent from "./components/routes/auth_routes/CartManagement/WalletHistoryComponent/CreditListComponent";
import CreditNoteListComponent from "./components/routes/auth_routes/CartManagement/WalletHistoryComponent/CreditNoteListComponent";
import DebitNoteListComponent from "./components/routes/auth_routes/CartManagement/WalletHistoryComponent/DebitNoteListComponent";
import AccountManagementPage from "./routes/auth_routes/AccountManagementPage";
import RefundRequestComponent from "./components/routes/auth_routes/AccountingManagement/RefundRequestComponent";
import ImageToUrlGeneration from "./components/ui/ImageToUrlGeneration";
import LabReportTable from "./components/routes/auth_routes/LabReportManagement/LabReportTableComponent";
import LabReportIndexPage from "./routes/auth_routes/LabReportManagementIndexPage";
import LabReportInterpretation from "./components/routes/auth_routes/LabReportInterpretation";
import VmsSupportComponent from "./components/routes/auth_routes/Root/Dashboard/VmsSupportComponent";
import RemindersTab from "./components/routes/auth_routes/CustomerProfile/CustomerDetails/Headers/RemindersTab/intex";
import RemindersCreation from "./components/routes/auth_routes/CustomerProfile/CustomerDetails/Headers/RemindersTab/RemindersCreation";
import DashboarComponent from "./components/routes/auth_routes/Root/Dashboard";
import PWLTable from "./components/routes/auth_routes/PatientWaitingList/PWLTable";
import AtHomeServicesTable from "./components/routes/auth_routes/AtHomeServices/AtHomeServicesTable";
import AddInventory from "./components/routes/auth_routes/InventoryManagement/AddInventory";
import MrpUpdate from "./components/routes/auth_routes/InventoryManagement/MrpUpdate";
import PaymentDetails from "./components/routes/auth_routes/AccountingManagement/PaymentDetails";
import AuditTrailDetailComponent from "./components/routes/auth_routes/InventoryManagement/AuditTrail/AuditTrailDetailComponent";
import MedicineHistoryTab from "./components/routes/auth_routes/CustomerProfile/CustomerDetails/Headers/MedicineHistoryTab";
import AuditTrailExportComponent from "./components/routes/auth_routes/InventoryManagement/AuditTrail/AuditTrailExportComponent";
import SlugManagement from "./components/ui/ContentManagementSystem";
import FinanceSummaryComponent from "./components/routes/auth_routes/AccountingManagement/FinanceSummaryComponent";
import DailyRevenuePaymentsComponent from "./components/routes/auth_routes/AccountingManagement/DailyRevenuePaymentsComponent";
import InvoiceAdjustmentComponent from "./components/routes/auth_routes/AccountingManagement/InvoiceAdjustmentComponent";
import FinanceSKUApprovalComponent from "./components/routes/auth_routes/AccountingManagement/FinanceSKUApprovalComponent";
import BillingMirrorComponent from "./components/routes/auth_routes/CartManagement/BillingMirrorComponent";
import VoucherSetupIndexPage from "./routes/auth_routes/VoucherSetupIndexPage";
import MembershipVoucherCreation from "./components/routes/auth_routes/MembershipVoucherSetup/MembershipVoucherCreation";
import MembershipVoucherList from "./components/routes/auth_routes/MembershipVoucherSetup/MembershipVoucherList";
import GiftSetupIndexPage from "./routes/auth_routes/GiftSetupIndexPage";
import GiftList from "./components/routes/auth_routes/GiftSetup/GiftList";
import GiftCreation from "./components/routes/auth_routes/GiftSetup/GiftCreation";
import HDCartPage from "./components/routes/auth_routes/HDCart/HDCartPage";
import ClinicCashClosing from "./components/routes/auth_routes/AccountingManagement/ClinicCashClosing";
import DayClosingIndexpage from "./routes/auth_routes/DayClosingIndexpage";
import DayClosingComponent from "./components/routes/auth_routes/ClinicClosingComponent/DayClosingComponent";
import FinancePurchaseOrder from "./components/routes/auth_routes/InventoryManagement/OrderPurchaseComponent/FinancePurchaseOrder";
import EMRTemplateCreationPage from "./components/routes/auth_routes/EMRTemplateCreationPage";
import ClinicGroomerMappingList from "./components/routes/auth_routes/Configuration/GroomerSetup/ClinicGroomerMappingList";
import ClinicParavetMappingList from "./components/routes/auth_routes/Configuration/ParavetSetup/ClinicParavetMappingList";

import LabPartnerListComponent from "./components/routes/auth_routes/LabReportManagement/LabPartnerOnboarding/LabPartnerListComponent";
import LabpartnerIndexPage from "./routes/auth_routes/WhitelistMobilePage";
import LabPartnerCreationComponent from "./components/routes/auth_routes/LabReportManagement/LabPartnerOnboarding/LabPartnerCreationComponent";
import LabPartnerClinicMapping from "./components/routes/auth_routes/LabReportManagement/LabPartnerMapping/LabPartnerClinicMapping";
import ClinicListComponent from "./components/routes/auth_routes/LabReportManagement/LabPartnerMapping/ClinicListComponent";
import CallAnalysisPage from "./routes/tools/CallAnalysisPage";
import CheckPrices from "./components/routes/auth_routes/Appointments/CheckPrices";
import PreferredDoctorMapping from "./components/routes/auth_routes/Appointments/PreferredDoctorMapping";
import AppointmentCartItemListingPage from "./components/routes/auth_routes/AppointmentCartItemMapping/AppointmentCartItemListingPage";
import AppointmentCartItemMappingIndexPage from "./routes/auth_routes/AppointmentCartItemMappingIndexPage";
import AppointmentCartItemMappingOnboardPage from "./components/routes/auth_routes/AppointmentCartItemMapping/AppointmentCartItemMappingOnboardPage";
import TilesConfigurationIndexPage from "./routes/auth_routes/TilesConfigurationPage";
import TilesConfigurationPage from "./components/routes/auth_routes/TilesConfiguration/TilesPage";

import { logLogoutDiagnostic } from "@/api_framework/logoutDiagnostics";

export interface IRouterProps {}

const Router: React.FC<IRouterProps> = () => {
  const { setVisible, setInvisible } = useModalActions("NO_INTERNET_MODAL");
  const networkState = useNetworkState();
  const configuration = useConfiguration();
  const { getRouteKey } = useRouter();
  const localCityId = localStorage.getItem(configuration.localStorage.CITY.key);
  const localClinicId = localStorage.getItem(configuration.localStorage.CLINIC.key);
  const dispatch = useAppDispatch();

  const redirectIfNotAuthenticated = () => {
    const accessToken = localStorage?.getItem(configuration?.localStorage?.ACCESS_TOKEN?.key);

    if (accessToken) {
      return true;
    } else {
      // This is the only exit that used to send a user to login without
      // recording anything. Which keys survived tells you what happened:
      //   token gone, others present -> something removed just the token
      //   everything gone            -> a localStorage.clear() ran
      //   nothing missing            -> a read-timing problem, not a deletion
      logLogoutDiagnostic("ROUTE_GUARD_NO_ACCESS_TOKEN", {
        attempted_path: window?.location?.pathname,
        had_clinic: !!localStorage.getItem(configuration?.localStorage?.CLINIC?.key),
        had_city: !!localStorage.getItem(configuration?.localStorage?.CITY?.key),
        had_user_details: !!localStorage.getItem(configuration?.localStorage?.USER_DETAILS?.key),
        localstorage_key_count: localStorage.length,
      });
      return redirect(getRouteKey("LOGIN_PAGE", "url"));
    }
  };

  const redirectIfLoggedIn = () => {
    const accessToken = localStorage?.getItem(configuration?.localStorage?.ACCESS_TOKEN?.key);
    if (accessToken) {
      return redirect(getRouteKey("SEARCH_CUSTOMER", "url"));
    } else {
      return false;
    }
  };

  const router = React.useMemo(
    () =>
      createBrowserRouter([
        {
          path: getRouteKey("LANDING_PAGE", "url"),
          element: <LandingPage />,
          errorElement: <RouteErrorBoundary fullPage />,
          loader: () => {
            return redirect(getRouteKey("LOGIN_PAGE", "url"));
          },
        },
        {
          path: getRouteKey("ROOT", "url"),
          element: <App />,
          errorElement: <RouteErrorBoundary fullPage />,
          loader: redirectIfNotAuthenticated,
          // Each page gets its own boundary, so a crash replaces only the page
          // and the shell around it stays usable.
          children: withRouteErrorBoundary([
            {
              path: getRouteKey("VMS_SETUP", "url"),
              element: <VMSSetupPage />,
            },
            {
              path: getRouteKey("INVALID_ACCESS", "path"),
              element: <InvalidAccessPage />,
            },
            {
              path: getRouteKey("INVALID_LOGIN", "path"),
              element: <InvalidLoginPage />,
            },
            {
              path: getRouteKey("CALL_ANALYSIS", "path"),
              element: <CallAnalysisPage />,
            },
            { path: getRouteKey("MAPPED_CLINIC_AND_CITY", "path"), element: <MappedClinicAndCityPage /> },
            {
              index: true,
              element: <DashboarComponent />,
            },
            {
              path: getRouteKey("DASHBOARD", "path"),
              element: <DashboarComponent />,
            },

            {
              path: getRouteKey("CUSTOMER_PROFILE", "path"),
              element: <CustomerProfilePage />,
              children: [
                {
                  index: false,
                  element: <CustomerProfileIndexPage />,
                },
                {
                  path: getRouteKey("ADD_CUSTOMER", "path"),
                  element: <CustomerAddPage />,
                },
                {
                  path: getRouteKey("SEARCH_CUSTOMER", "path"),
                  element: <CustomerProfileSearchPage />,
                },
                {
                  path: getRouteKey("USER_LEVEL_COUPON", "path"),
                  element: <UserLevelCouponPage />,
                },

                {
                  path: getRouteKey("CUSTOMER_SUPPORT", "path"),
                  element: <VmsSupportComponent />,
                },
                {
                  path: `${getRouteKey("DETAILS_CUSTOMER", "path")}/:user_id?/:pet_id?`,
                  element: <CustomerDetailsPage />,
                  children: [
                    // Actions ROUTES
                    {
                      path: `${getRouteKey("EDIT_CUSTOMER", "path")}/:user_id?/:pet_id?`,
                      element: <EditCustomerInfoForm />,
                    },
                    {
                      path: `${getRouteKey("EDIT_PET", "path")}/:user_id?/:pet_id?`,
                      element: <EditCustomerPetInfoForm />,
                    },
                    {
                      path: `${getRouteKey("ADD_PET", "path")}/:user_id?/:pet_id?`,
                      element: <AddPetForCustomerForm />,
                    },
                    // TAB Routes
                    {
                      path: `${getRouteKey("APPOINTMENTS_TAB", "path")}/:user_id?/:pet_id?`,
                      element: <AppointmentTab />,
                    },
                    {
                      path: `${getRouteKey("OTHER_INFORMATION_TAB", "path")}/:user_id?/:pet_id?`,
                      element: <OtherInformationTab />,
                    },
                    {
                      path: `${getRouteKey("VACCINATION_TAB", "path")}/:user_id?/:pet_id?`,
                      element: <VaccinationTab />,
                    },
                    {
                      path: `${getRouteKey("MEDICINE_HISTORY", "path")}/:user_id?/:pet_id?`,
                      element: <MedicineHistoryTab />,
                    },
                    {
                      path: `${getRouteKey("REMINDERS_TAB", "path")}/:user_id?/:pet_id?`,
                      element: <RemindersTab />,
                    },
                    {
                      path: `${getRouteKey("REMINDERS_CREATION", "path")}/:user_id?/:pet_id?`,
                      element: <RemindersCreation />,
                    },
                    {
                      path: `${getRouteKey("EMR_TAB", "path")}/:user_id?/:pet_id?`,
                      element: <EMRTab />,
                    },
                    {
                      path: `${getRouteKey("LAB_TEST_TAB", "path")}/:user_id?/:pet_id?`,
                      element: <LabTestTab />,
                    },
                    {
                      path: `${getRouteKey("LAB_INTERPRETATION_TAB", "path")}/:user_id?/:pet_id?`,
                      element: <LabInterpretationTab />,
                    },
                    {
                      path: `${getRouteKey("CERTIFICATES_TAB", "path")}/:user_id?/:pet_id?`,
                      element: <CertificatesTab />,
                    },
                    {
                      path: `${getRouteKey("ACTIVITY_TAB", "path")}/:user_id?/:pet_id?`,
                      element: <ActivityTab />,
                    },
                    {
                      path: `${getRouteKey("ACCOUNT_TAB", "path")}/:user_id?/:pet_id?`,
                      element: <AccountTab />,
                    },
                    {
                      path: `${getRouteKey("INVOICE_TAB", "path")}/:user_id?/:pet_id?`,
                      element: <InvoiceListingComponent />,
                    },
                    {
                      path: `${getRouteKey("TICKECT_TAB", "path")}/:user_id?/:pet_id?`,
                      element: <TicketTab />,
                    },
                    {
                      path: `${getRouteKey("MEMBERSHIP_DETAILS_TAB", "path")}/:user_id?/:pet_id?`,
                      element: <MembershipDetailsTab />,
                    },
                    {
                      path: `${getRouteKey("FORM_TAB", "path")}/:user_id?/:pet_id?`,
                      element: <FormTabComponent />,
                    },
                  ],
                },
                {
                  path: `${getRouteKey("BILLING_MIRROR_VIEW", "path")}`,
                  element: <BillingMirrorComponent />,
                },
                {
                  path: getRouteKey("EMR_CREATION", "path"),
                  element: <EMRTemplateCreationPage />,
                },
              ],
            },
            {
              path: getRouteKey("APPOINTMENTS", "path"),
              element: <AppointmentsPage />,
              children: [
                {
                  path: getRouteKey("CHECK_SLOTS", "path"),
                  element: <CheckSlotsComponent />,
                  children: [
                    {
                      path: `${getRouteKey("CHECK_AVAILABLE_SLOTS", "path")}`,
                      element: <CheckAvailableSlotsComponent />,
                    },
                    {
                      path: `${getRouteKey("CHECK_BOOKED_SLOTS", "path")}`,
                      element: <CheckBookedSlotsComponent />,
                    },
                  ],
                },
                {
                  path: getRouteKey("CHECK_PRICES", "path"),
                  element: <CheckPrices />,
                },
                {
                  path: getRouteKey("PREFERRED_DOCTORS", "path"),
                  element: <PreferredDoctorMapping />,
                },
                {
                  path: getRouteKey("OVERBOOKING", "path"),
                  element: <AppointmentOverBookingComponent />,
                },
                {
                  path: getRouteKey("OVERBOOKING_V2", "path"),
                  element: <AppointmentOverBookingV2Component />,
                },
                {
                  path: `${getRouteKey("OVERBOOKING_V2", "path")}/create`,
                  element: <AppointmentOverBookingV2CreateRequestRoute />,
                },
                {
                  path: `${getRouteKey("OVERBOOKING_V2", "path")}/update`,
                  element: <AppointmentOverBookingV2UpdateRequestRoute />,
                },
                {
                  path: getRouteKey("OVER_BOOKING_PERMISION_LIST", "path"),
                  element: <OverBookingPermisionListComponent />,
                },
                {
                  path: getRouteKey("HAIRCUT_TO_BATH", "path"),
                  element: <AppointmentHairCutToBathComponent />,
                },
                {
                  path: getRouteKey("APPOINTMENT_SHIFT", "path"),
                  element: <AppointmentShiftAppointment />,
                },
                {
                  path: getRouteKey("CHARGEABLE_BOOKING_LIST", "path"),
                  element: <ChargeableBookingsList />,
                },
                {
                  path: getRouteKey("PRODUCT_REQUIREMENT_FORM", "path"),
                  element: <ProductRequirementForm />,
                },
              ],
            },
            {
              path: `${getRouteKey("SLOTS", "path")}/:user_id?/:pet_id?`,
              element: <SlotsPage />,
              children: [
                {
                  path: `${getRouteKey("BOOKING_CONFIRMATION", "path")}/:user_id?/:pet_id?`,
                  element: <SlotsConfirmationComponent />,
                },
                {
                  path: `${getRouteKey("AVAILABLE_SLOTS", "path")}/:user_id?/:pet_id?`,
                  element: <AvailableAndBookedSlotsComponent />,
                },
                {
                  path: `${getRouteKey("BOOKED_SLOTS", "path")}/:user_id?/:pet_id?`,
                  element: <BookedSlotsComponent />,
                },

                {
                  path: `${getRouteKey("OVERBOOKING_SLOTS", "path")}/:user_id?/:pet_id?`,
                  element: <OverBookingSlotsComponent />,
                },
              ],
            },
            {
              path: getRouteKey("PATIENT_WAITING_LIST", "path"),
              element: <PatientWaitingListPage />,
              children: [
                {
                  index: true,
                  element: <PWLTable />,
                },
              ],
            },
            {
              path: getRouteKey("AT_HOME_SERVICES", "path"),
              element: <AtHomeServicesPage />,
              children: [
                {
                  index: true,
                  element: <AtHomeServicesTable />,
                },
              ],
            },
            {
              path: getRouteKey("STAFF_CALENDAR", "path"),
              element: <StaffCalendarComponentIndex />,
              children: [
                {
                  index: true,
                  element: <StaffCalendarComponent />,
                },
              ],
            },
            {
              path: getRouteKey("ORDER_MANAGEMENT", "path"),
              element: <OrderManagementIndexPage />,
              children: [
                {
                  index: true,
                  element: <CentralOppsModuleComponent />,
                },
              ],
            },
            {
              path: `${getRouteKey("CART_MANAGEMENT", "path")}/:user_id?/:pet_id?`,
              element: <CartPage />,
              children: [
                {
                  path: `${getRouteKey("CREDIT_NOTE", "path")}/:user_id?`,
                  element: <CreditNoteListComponent />,
                },
                {
                  path: `${getRouteKey("DEBIT_NOTE", "path")}/:user_id?`,
                  element: <DebitNoteListComponent />,
                },
                {
                  path: `${getRouteKey("INVOICE_LIST", "path")}/:user_id?`,
                  element: <InvoiceTab />,
                },
                {
                  path: `${getRouteKey("TRANSACTION_HISTORY", "path")}/:user_id?`,
                  element: <TransactionHistoryComponent />,
                },
                {
                  path: `${getRouteKey("CREDIT_LIST", "path")}/:user_id?`,
                  element: <CreditListComponent />,
                },
                {
                  path: `${getRouteKey("ADD_CREDIT", "path")}/:user_id?`,
                  element: <AddCreditComponent />,
                },
                {
                  path: `${getRouteKey("CART_LIST", "path")}/:user_id?`,
                  element: <CartListComponent />,
                },
                {
                  path: `${getRouteKey("CART_CREATION", "path")}/:user_id?/:cart_id?`,
                  element: <CartCreationComponent />,
                },
                {
                  path: `${getRouteKey("CART_BILLING", "path")}/:user_id?/:cart_id?`,
                  element: <CartBillingComponent />,
                },
                {
                  path: `${getRouteKey("HD_CART_PAGE", "path")}/:user_id?`,
                  element: <HDCartPage />,
                },
              ],
            },
            {
              path: getRouteKey("TRANSFER_ORDERS", "path"),
              element: <ClinicToClinicTranfer />,
              children: [
                {
                  index: true,
                  element: <ClinicToClinicTransfer />,
                },
              ],
            },
            {
              path: getRouteKey("ROTA", "path"),
              element: <RotaManagementComponentIndex />,
              children: [
                {
                  index: true,
                  element: <RotaManagementComponent />,
                },
                {
                  path: getRouteKey("SHIFT_DETAILS", "path"),
                  element: <ShiftDetailsComponent />,
                },
                {
                  path: getRouteKey("SHIFT_ADD", "path"),
                  element: <AddNewShiftComponent />,
                },
                {
                  path: getRouteKey("SHIFT_EDIT", "path"),
                  element: <AddNewShiftComponent />,
                },
                {
                  path: getRouteKey("ROTA_VIEW", "path"),
                  element: <RotaCalenderComponent />,
                },
                {
                  path: getRouteKey("ROTA_ERROR", "path"),
                  element: <RotaErrorListComponent />,
                },
                {
                  path: getRouteKey("REMOVE_SLOTS", "path"),
                  element: <RemoveSlotsComponent />,
                },
                {
                  path: `${getRouteKey("REMOVE_SLOTS", "path")}/create`,
                  element: <RemoveSlotsCreateRequestRoute />,
                },
                {
                  path: getRouteKey("BOOKING_FEE_CONFIG", "path"),
                  element: <BookingFeeConfigListComponent />,
                },
                {
                  path: `${getRouteKey("BOOKING_FEE_CONFIG", "path")}/create`,
                  element: <BookingFeeConfigComponent />,
                },
              ],
            },
            {
              path: getRouteKey("CONFIGURATION", "path"),
              element: <ConfigurationPage />,
              children: [
                {
                  index: true,
                  element: <ConfigurationIndexPage />,
                },
                {
                  path: getRouteKey("CLINIC_SETUP", "path"),
                  element: <ConfigurationClinicSetup />,
                },
                {
                  path: `${getRouteKey("CLINIC_SETUP_ONBOARD", "path")}/:clinic_id?`,
                  element: <ConfigurationClinicOnboard />,
                },
                {
                  path: `${getRouteKey("CLINIC_SETUP_IMAGE_UPDATE", "path")}/:clinic_id`,
                  element: <ClinicImagesSetup />,
                },
                {
                  path: getRouteKey("CITY_SETUP", "path"),
                  element: <ConfigurationCitySetup />,
                },
                {
                  path: getRouteKey("CITY_SETUP_ONBOARD", "path"),
                  element: <ConfigurationCityOnboard />,
                },
                {
                  path: getRouteKey("EQUIPMENT_SETUP", "path"),
                  element: <ConfigurationEquipmentSetup />,
                },
                {
                  path: getRouteKey("EQUIPMENT_SETUP_ONBOARD", "path"),
                  element: <ConfigurationEquipmentOnboard />,
                },
                {
                  path: getRouteKey("WAREHOUSE_LIST", "path"),
                  element: <WarehouseOnboardComponent />,
                },
                {
                  path: getRouteKey("WAREHOUSE_ONBOARD", "path"),
                  element: <WarehouseAddComponent />,
                },
                {
                  path: getRouteKey("STORE_TIMING_SETUP", "path"),
                  element: <StoreTimingSetup />,
                },
                {
                  path: getRouteKey("PINCODE_SETUP", "path"),
                  element: <QuickCommercePincodeSetup />,
                },
                {
                  path: getRouteKey("PINCODE_ONBOARD", "path"),
                  element: <ConfigurationAddPincodeOnboard />,
                },
                {
                  path: getRouteKey("PINCODE_WEATHER_SETUP", "path"),
                  element: <PincodeWeatherSetup />,
                },
                {
                  path: getRouteKey("HOME_TILE_SETUP", "path"),
                  element: <HomeTileConfigSetup />,
                },
                {
                  path: getRouteKey("HOME_TILE_ONBOARD", "path"),
                  element: <HomeTileOnboard />,
                },
                {
                  path: getRouteKey("CLINIC_TAG_SETUP", "path"),
                  element: <ClinicTagConfigSetup />,
                },
                {
                  path: getRouteKey("CLINIC_TAG_ONBOARD", "path"),
                  element: <ClinicTagOnboard />,
                },
                // {
                //   path: getRouteKey("SERVICE_SETUP", "path"),
                //   element: <ConfigurationServiceSetup />,
                // },
                // {
                //   path: getRouteKey("SERVICE_SETUP_ONBOARD", "path"),
                //   element: <ConfigurationServiceOnboard />,
                // },
                {
                  path: getRouteKey("STAFF_SETUP", "path"),
                  element: <ConfigurationStaffSetup />,
                },
                {
                  path: getRouteKey("GROOMER_SETUP", "path"),
                  element: <ConfigurationGroomerSetup />,
                },
                {
                  path: getRouteKey("GROOMER_SETUP_ONBOARD", "path"),
                  element: <GroomerOnboardComponent />,
                },
                {
                  path: getRouteKey("GROOMER_CLINIC_MAP", "path"),
                  element: <ClinicGroomerMappingList />,
                },
                {
                  path: getRouteKey("STAFF_ONBOARDING", "path"),
                  element: <StaffOnboardingList />,
                },
                {
                  path: getRouteKey("STAFF_ONBOARDING_FORM", "path"),
                  element: <StaffOnboardForm />,
                },
                {
                  path: getRouteKey("STAFF_CLINIC_MAP", "path"),
                  element: <ClinicMappingList />,
                },
                {
                  path: getRouteKey("PARAVET_SETUP", "path"),
                  element: <ConfigurationParavetSetup />,
                },
                {
                  path: getRouteKey("PARAVET_SETUP_ONBOARD", "path"),
                  element: <ParavetOnboardComponent />,
                },
                {
                  path: getRouteKey("PARAVET_CLINIC_MAP", "path"),
                  element: <ClinicParavetMappingList />,
                },
                {
                  path: getRouteKey("STAFF_SETUP_ONBOARD", "path"),
                  element: <ConfigurationStaffOnboard />,
                },
                {
                  path: getRouteKey("STAFF_CATEGORY_SETUP", "path"),
                  element: <ConfigurationStaffCategorySetup />,
                },
                {
                  path: getRouteKey("STAFF_CATEGORY_SETUP_ONBOARD", "path"),
                  element: <ConfigurationStaffCategoryOnboard />,
                },
                {
                  path: getRouteKey("ROLES_PERMISSION", "path"),
                  element: <ConfigurationRolesSetup />,
                },
                {
                  path: getRouteKey("ROLES_PERMISSION_ONBOARD", "path"),
                  element: <ConfigurationRolesOnboard />,
                },
              ],
            },
            {
              path: getRouteKey("INVENTORY", "path"),
              element: <InventoryManagementPage />,
              children: [
                {
                  path: getRouteKey("HSN_AND_TAX_SETUP", "path"),
                  element: <TaxSetupComponent />,
                },
                {
                  path: getRouteKey("TAX_ADD", "path"),
                  element: <TaxAddComponent />,
                },
                {
                  path: getRouteKey("BATCH_SETUP", "path"),
                  element: <BatchSetupComponent />,
                },
                {
                  path: getRouteKey("BATCH_ADD", "path"),
                  element: <BatchAddComponent />,
                },
                {
                  path: getRouteKey("MANUFACTURER_SETUP", "path"),
                  element: <ManufacturerSetupComponent />,
                },
                {
                  path: getRouteKey("MANUFACTURER_ADD", "path"),
                  element: <ManufacturerAddComponent />,
                },
                {
                  path: getRouteKey("VENDORS_LIST", "path"),
                  element: <VendorsListComponent />,
                },
                {
                  path: getRouteKey("VENDOR_ADD", "path"),
                  element: <VendorAddComponent />,
                },
                {
                  path: getRouteKey("PRODUCT_LIST", "path"),
                  element: <ProductListComponent />,
                },
                {
                  path: getRouteKey("PRODUCT_ADD", "path"),
                  element: <ProductAddComponent />,
                },
                {
                  path: getRouteKey("PRODUCT_PERSONA_DISCOUNT", "path"),
                  element: <ProductPersonaDiscountComponent />,
                },
                {
                  path: getRouteKey("SERVICE_LIST", "path"),
                  element: <ServiceListComponent />,
                },
                {
                  path: getRouteKey("SERVICE_ADD", "path"),
                  element: <ServiceAddComponent />,
                },
                {
                  path: getRouteKey("PURCHASE_LIST", "path"),
                  element: <OrderPurchaseListComponent />,
                },
                {
                  path: getRouteKey("PURCHASE_ADD", "path"),
                  element: <OrderPurchaseAddComponent />,
                },
                {
                  path: getRouteKey("GRN_DONE", "path"),
                  element: <GoodsReceiptNoteGrnDoneComponent />,
                },
                {
                  path: getRouteKey("PENDING_GOODS", "path"),
                  element: <GoodsReceiptNotePendingGoodsComponent />,
                },
                {
                  path: getRouteKey("STOCK_INCOMPLETE_FORM", "path"),
                  element: <StockIncompleteForm />,
                },
                {
                  path: getRouteKey("INVENTORY_SNAPSHOT", "path"),
                  element: <InventorySnapshot />,
                },
                {
                  path: getRouteKey("DOCTOR_INVENTORY", "path"),
                  element: <DoctorInventory />,
                },
                {
                  path: getRouteKey("ACTIONS_ON_GOOD_INVENTORY", "path"),
                  element: <GoodInventoryActions />,
                },
                {
                  path: getRouteKey("BAD_INVENTORY_REQUESTS", "path"),
                  element: <BadInventoryRequests />,
                },
                {
                  path: getRouteKey("TRANSFER_GOOD_INVENTORY", "path"),
                  element: <TransferInventory />,
                },
                {
                  path: getRouteKey("TRANSFER_BAD_INVENTORY", "path"),
                  element: <TransferInventory />,
                },
                {
                  path: getRouteKey("TRANSFER_ORDER_READ_ONLY", "path"),
                  element: <TransferOrderReadOnly />,
                },
                {
                  path: `${getRouteKey("TRANSFER_ORDER_DETAILS", "path")}/:transfer_order_id`,
                  element: <TransferOrderDetails />,
                },
                {
                  path: getRouteKey("AHS_INVENTORY_TRANSFER", "path"),
                  element: <ClinicToDoctorTransferComponent />,
                },
                {
                  path: getRouteKey("AHS_CREATE_INVENTORY_TRANSFER", "path"),
                  element: <DoctorTransferInventory />,
                },
                {
                  path: getRouteKey("BOM_SETUP", "path"),
                  element: <BOMSetupListing />,
                },
                {
                  path: `${getRouteKey("BOM_ONBOARD", "path")}`,
                  element: <BOMSetupOnboard />,
                },
                {
                  path: getRouteKey("ITEM_GROUP_SETUP", "path"),
                  element: <ItemGroupSetupListing />,
                },
                {
                  path: `${getRouteKey("ITEM_GROUP_ONBOARD", "path")}/:item_group_id?`,
                  element: <ItemGroupSetupOnboard />,
                },
                {
                  path: getRouteKey("SURGE_PRICING_LIST", "path"),
                  element: <SurgePricingList />,
                },
                {
                  path: getRouteKey("SURGE_PRICING_ONBOARD", "path"),
                  element: <SurgeRuleOnboard />,
                },
                {
                  path: getRouteKey("SURGE_PRICING_PRICE_CHECK", "path"),
                  element: <SurgePriceCheck />,
                },
                {
                  path: getRouteKey("CATEGORY_SETUP", "path"),
                  element: <CategorySetupListing />,
                },
                {
                  path: `${getRouteKey("CATEGORY_ONBOARD", "path")}/:category_id?`,
                  element: <CategorySetupOnboard />,
                },
                {
                  path: getRouteKey("SUB_CATEGORY_SETUP", "path"),
                  element: <SubCategorySetupListing />,
                },
                {
                  path: `${getRouteKey("SUB_CATEGORY_ONBOARD", "path")}/:sub_category_id?`,
                  element: <SubCategorySetupOnboard />,
                },
                {
                  path: getRouteKey("CATEGORY_MAPPING", "path"),
                  element: <CategorySubCategoryMapping />,
                },
                {
                  path: `${getRouteKey("CATEGORY_MAPPING_ONBOARD", "path")}/:category_mapping_id?`,
                  element: <CategorySubCategoryMappingOnboard />,
                },
                {
                  path: getRouteKey("ADD_INVENTORY", "path"),
                  element: <AddInventory />,
                },
                {
                  path: getRouteKey("MRP_UPDATE", "path"),
                  element: <MrpUpdate />,
                },
                {
                  path: getRouteKey("AUDIT_TRAIL", "path"),
                  element: <AuditTrailDetailComponent />,
                },
                {
                  path: getRouteKey("AUDIT_TRAIL_EXPORT_CSV", "path"),
                  element: <AuditTrailExportComponent />,
                },
              ],
            },
            {
              path: getRouteKey("ACCOUNTING", "path"),
              element: <AccountManagementPage />,
              children: [
                {
                  path: getRouteKey("REFUND_REQUEST", "path"),
                  element: <RefundRequestComponent />,
                },
                {
                  path: getRouteKey("FINANCE_SUMMARY", "path"),
                  element: <FinanceSummaryComponent />,
                },
                {
                  path: getRouteKey("DAILY_REVENUE_PAYMENTS", "path"),
                  element: <DailyRevenuePaymentsComponent />,
                },
                {
                  path: getRouteKey("FINANCE_SKU_APPROVAL", "path"),
                  element: <FinanceSKUApprovalComponent />,
                },
                {
                  path: getRouteKey("DEBIT_NOTE_REQUEST", "path"),
                  element: <DebitNoteListComponent />,
                },
                {
                  path: getRouteKey("UPDATE_PAYMENT_DETAILS", "path"),
                  element: <PaymentDetails />,
                },
                {
                  path: getRouteKey("CASH_CLOSING", "path"),
                  element: <ClinicCashClosing />,
                },
                {
                  path: getRouteKey("FINANCE_PURCHASE_ORDER", "path"),
                  element: <FinancePurchaseOrder />,
                },
                {
                  path: getRouteKey("FORCE_DISCOUNT", "path"),
                  // key: the two tabs share a component; switching must not carry state over
                  element: <InvoiceAdjustmentComponent key="FORCE_DISCOUNT" requestType="FORCE_DISCOUNT" />,
                },
                {
                  path: getRouteKey("BAD_DEBT", "path"),
                  element: <InvoiceAdjustmentComponent key="BAD_DEBT" requestType="BAD_DEBT" />,
                },
              ],
            },
            {
              path: getRouteKey("DAY_CLOSING", "path"),
              element: <DayClosingIndexpage />,
              children: [
                {
                  path: getRouteKey("CLINIC_CLOSING", "path"),
                  element: <DayClosingComponent />,
                },
              ],
            },
            {
              path: getRouteKey("LAB_REPORT", "path"),
              element: <LabReportIndexPage />,
              children: [
                {
                  path: getRouteKey("INTERNAL_LAB_REPORT", "path"),
                  element: <LabReportTable />,
                },
                {
                  path: getRouteKey("EXTERNAL_LAB_REPORT", "path"),
                  element: <LabReportTable />,
                },
                {
                  path: getRouteKey("INCOMING_LAB_REPORT", "path"),
                  element: <LabReportTable />,
                },
              ],
            },
            {
              path: getRouteKey("LAB_REPORT_INTERPRETATION", "path"),
              children: [
                {
                  path: getRouteKey("LAB_REPORT_INTERPRETATION_LIST", "path"),
                  element: <LabReportInterpretation />,
                },
              ],
            },
            {
              path: getRouteKey("SERVICE_DESK", "path"),
              element: <VeticServiceDeskIndex />,
              children: [
                {
                  path: getRouteKey("SERVECE_DESK_RAISE_ISSUE_LIST", "path"),
                  element: <ServiceDeskissueList />,
                },
                {
                  path: getRouteKey("SERVECE_DESK_RAISE_ISSUE", "path"),
                  element: <ServiceDeskRaiseIssue />,
                },
              ],
            },
            {
              path: getRouteKey("COUPON_SERVICE", "path"),
              element: <CouponServiceIndexPage />,
              children: [
                {
                  path: getRouteKey("COUPON_SERVICE_LIST", "path"),
                  element: <CouponListComponent />,
                },
                {
                  path: getRouteKey("COUPON_SERVICE_CREATION", "path"),
                  element: <CouponCreationComponent />,
                },
              ],
            },
            {
              path: getRouteKey("MEMBERSHIP_SETUP", "path"),
              element: <MembershipSetupIndexPage />,
              children: [
                {
                  path: getRouteKey("MEMBERSHIP_SETUP_LIST", "path"),
                  element: <MembershipSetup />,
                },
                {
                  path: getRouteKey("MEMBERSHIP_SETUP_CREATION", "path"),
                  element: <MembershipCreationComponent />,
                },
              ],
            },
            {
              path: getRouteKey("MEMBERSHIP_EXTENSION", "path"),
              element: <MembershipExtensionIndexPage />,
              children: [
                {
                  path: getRouteKey("MEMBERSHIP_EXTENSION_LIST", "path"),
                  element: <MembershipExtensionSetup />,
                },
                {
                  path: getRouteKey("MEMBERSHIP_EXTENSION_CREATION", "path"),
                  element: <MembershipExtensionCreationPage />,
                },
              ],
            },
            {
              path: getRouteKey("VOUCHER_SETUP", "path"),
              element: <VoucherSetupIndexPage />,
              children: [
                {
                  path: getRouteKey("VOUCHER_SETUP_LIST", "path"),
                  element: <MembershipVoucherList />,
                },
                {
                  path: getRouteKey("VOUCHER_SETUP_CREATION", "path"),
                  element: <MembershipVoucherCreation />,
                },
              ],
            },
            {
              path: getRouteKey("GIFT_SETUP", "path"),
              element: <GiftSetupIndexPage />,
              children: [
                {
                  path: getRouteKey("GIFT_SETUP_LIST", "path"),
                  element: <GiftList />,
                },
                {
                  path: getRouteKey("GIFT_SETUP_CREATION", "path"),
                  element: <GiftCreation />,
                },
              ],
            },
            {
              path: getRouteKey("WHITELIST_MOBILE", "path"),
              element: <WhiteListComponentIndex />,
              children: [
                {
                  path: getRouteKey("WHITELIST_MOBILE_ONBOARD", "path"),
                  element: <WhitelistMobile />,
                },
                {
                  path: getRouteKey("IMAGE_GENERATION", "path"),
                  element: <ImageToUrlGeneration />,
                },
                {
                  path: getRouteKey("SLUG_MANAGEMENT", "path"),
                  element: <SlugManagement />,
                },
              ],
            },
            {
              path: getRouteKey("CLINIC_LAB_REPORT", "path"),
              element: <LabpartnerIndexPage />,
              children: [
                {
                  path: getRouteKey("LAB_PARTNER_LIST", "path"),
                  element: <LabPartnerListComponent />,
                },
                {
                  path: getRouteKey("LAB_PARTNER_ONBOARD", "path"),
                  element: <LabPartnerCreationComponent />,
                },
                {
                  path: getRouteKey("LAB_PARTNER_CLINIC_LIST", "path"),
                  element: <ClinicListComponent />,
                },
                {
                  path: getRouteKey("LAB_PARTNER_CLINIC_MAPPING", "path"),
                  element: <LabPartnerClinicMapping />,
                },
              ],
            },
            {
              path: getRouteKey("DATA_MAPPINGS", "path"),
              element: <AppointmentCartItemMappingIndexPage />,
              children: [
                {
                  path: getRouteKey("APPOINTMENT_CART_ITEM_MAPPING", "path"),
                  element: <AppointmentCartItemListingPage />,
                },
                {
                  path: getRouteKey("APPOINTMENT_CART_ITEM_MAPPING_ONBOARD", "path"),
                  element: <AppointmentCartItemMappingOnboardPage />,
                },
              ],
            },
          ]),
        },
        {
          path: getRouteKey("LOGIN_PAGE", "url"),
          loader: redirectIfLoggedIn,
          element: <LoginPage />,
          errorElement: <RouteErrorBoundary fullPage />,
        },
        {
          path: getRouteKey("LOGOUT_PAGE", "url"),
          element: <LogoutPage />,
          errorElement: <RouteErrorBoundary fullPage />,
        },
      ]),
    [],
  );

  // When router mounts
  useEffect(() => {
    try {
      const PWLFilters = localStorage.getItem(configuration.localStorage.PWL_FILTERS.key);

      const accessTokenLocalStorageValue: string | null = localStorage.getItem(
        configuration.localStorage.ACCESS_TOKEN.key,
      );
      const sessionUserDetailsValue: string | null = localStorage.getItem(configuration.localStorage.USER_DETAILS.key);

      if (accessTokenLocalStorageValue && sessionUserDetailsValue) {
        if (PWLFilters) {
          const filters = JSON.parse(PWLFilters) as IPatientWaitingListFiltersParamsBody;
          dispatch(
            setPWLFilters({
              filters: filters,
            }),
          );
        }
        dispatch(
          setAuthorization({
            accessToken: accessTokenLocalStorageValue,
            isValid: true,
          }),
        );

        const sessionUserDetails: ISessionUserDetails = JSON.parse(sessionUserDetailsValue);
        dispatch(
          setOAuthUser({
            ...sessionUserDetails,
          }),
        );
      }
    } catch {
      toast.error("Session Error, Loggin out");
    }
  }, [dispatch]);

  useEffect(() => {
    if (networkState.online) {
      setInvisible();
    } else {
      setVisible();
    }
  }, [networkState.online]);

  useEffect(() => {
    if (localCityId && localClinicId) {
      /* Sets mapped clinic and cities here after each login or city or loclclicni id has been 
      changed so that it reflects to every other components as well where it has been used. */
      const cityId = localStorage.getItem(configuration.localStorage.CITY.key);
      const clinicId = localStorage.getItem(configuration.localStorage.CLINIC.key);
      const clinicName = localStorage.getItem(configuration.localStorage.CLINIC_NAME.key);
      dispatch(setMappedCityId(cityId));
      dispatch(
        setMappedClinicData({
          id: clinicId,
          name: clinicName,
        }),
      );
    } else {
      // setCityAndClinicModalVisible();
    }
  }, [localCityId, localClinicId]);

  // useEffect(() => {
  //   if (loggedInUser) {
  //     const appVersion = localStorage.getItem(configuration.localStorage.APP_VERSION.key);

  //     if (!appVersion) {
  //       installAppVersion();
  //     }
  //     if (appVersion !== configuration.env.version) {
  //       installAppVersion();
  //     }
  //   }
  // }, [loggedInUser]);

  // useEffect(() => {
  //   if (stateAccessToken && stateAccessToken?.length > 0) {
  //     const accessToken = localStorage.getItem(configuration.localStorage.ACCESS_TOKEN.key);
  //     if (loggedInUser?.email && accessToken) {
  //       fetchUserPermissions(loggedInUser?.email);
  //     }
  //   }
  // }, [stateAccessToken]);

  return (
    <>
      <Toaster position="top-right" />
      <RouterProvider router={router} />
    </>
  );
};

export default Router;
